<template>
  <header class="top-bar">
    <div class="top-bar__edge">
      <div class="top-bar__burger">
        <IconButton v-bind="iconConfig" data-cy="burger-menu" icon="burger" @click="toggleIsSideBarMinimized()" />
      </div>

      <div class="top-bar__logo" data-cy="header_logo">
        <a :href="homePage" class="top-bar__logo-link">
          <UgIcon icon="usergram" width="147px" height="30px" />
        </a>
      </div>
    </div>

    <div v-if="isAvailableUserList" class="top-bar__search">
      <SearchNavigation data-cy="header_search-navigation" />
    </div>

    <div class="top-bar__menu">
      <div class="top-bar__menu-item">
        <IconButton
          icon="one-user"
          v-bind="iconConfig"
          @mouseover="showAccountBalloon"
          @mouseout="hideAccountBalloon"
        />
        <TopBarMenuAccountBalloon
          v-show="isShowingAccountBalloon"
          class="top-bar__balloon"
          @mouseover="showAccountBalloon"
          @mouseout="hideAccountBalloon"
        />
      </div>
      <div class="top-bar__menu-item">
        <IconButton icon="help" v-bind="iconConfig" @mouseover="showHelpBalloon" @mouseout="hideHelpBalloon" />
        <TopBarMenuHelpBalloon
          v-show="isShowingHelpBalloon"
          class="top-bar__balloon"
          @mouseover="showHelpBalloon"
          @mouseout="hideHelpBalloon"
        />
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { UgIcon, IconButton } from "@bebit/atomic";
import { LoginUser, authStore } from "@bebit/auth";
import SearchNavigation from "search/SearchNavigation.vue";
import { ref, computed } from "vue";
import TopBarMenuAccountBalloon from "@host/components/top-bar/TopBarMenuAccountBalloon.vue";
import TopBarMenuHelpBalloon from "@host/components/top-bar/TopBarMenuHelpBalloon.vue";
import { sideBarStore } from "@host/store/side-bar";

const HIDE_BALLOON_TIMEOUT_MS = 100;

const { toggleIsSideBarMinimized } = sideBarStore();

const isShowingHelpBalloon = ref(false);
let helpBalloonTimeout: ReturnType<typeof setTimeout>;
let accountBalloonTimeout: ReturnType<typeof setTimeout>;

const homePage = window.location.origin;

const user: LoginUser = authStore.user as LoginUser;

const isAvailableUserList = computed<boolean>(() => {
  return user.permissions.isAvailableUserList;
});

function showHelpBalloon() {
  clearTimeout(helpBalloonTimeout);
  isShowingHelpBalloon.value = true;
}

function hideHelpBalloon() {
  helpBalloonTimeout = setTimeout(() => {
    isShowingHelpBalloon.value = false;
  }, HIDE_BALLOON_TIMEOUT_MS);
}

const isShowingAccountBalloon = ref(false);

function showAccountBalloon() {
  clearTimeout(accountBalloonTimeout);
  isShowingAccountBalloon.value = true;
}

function hideAccountBalloon() {
  accountBalloonTimeout = setTimeout(() => {
    isShowingAccountBalloon.value = false;
  }, HIDE_BALLOON_TIMEOUT_MS);
}

const iconConfig = {
  hoveredBackgroundColor: "#e5f4ff",
  width: 36,
  height: 36,
  iconScale: 0.45,
  normalIconColor: "#323442",
};
</script>

<style lang="scss" scoped>
.top-bar {
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 0 14px;
  width: 100%;
  height: 70px;
  border-bottom: 1px solid $colorBase500;
  background-color: $colorWhite;
  min-width: 1280px;
}

@media print {
  .top-bar {
    display: none;
  }
}

.top-bar__edge {
  display: flex;
  align-items: center;
  min-width: 210px;
}

.top-bar__burger {
  display: flex;
  height: 36px;
  justify-content: center;
  width: 60px;
  :deep(.top-bar__burger-icon) {
    border-radius: 50%;
    cursor: pointer;
    height: 36px;
    width: 36px;
  }
}
.top-bar__logo {
  display: inline-flex;
  align-items: center;
  width: 147px;
  height: 30px;
}

.top-bar__logo-link {
  display: inline-flex;
}

.top-bar__search {
  position: relative;
  margin: 0 30px;
  min-width: 520px;
  max-width: 980px;
  width: 100%;
}

.top-bar__menu {
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  justify-content: right;
  min-width: 210px;
  padding-right: 20px;
}

.top-bar__menu-item {
  position: relative;

  :deep(.top-bar__balloon) {
    position: absolute;
    right: -8px;
    top: 111%;
  }
}
</style>
